/**
 * Agent Workspace Component
 * 
 * Main container component for the agent-related UI.
 * Contains the sidebar navigation and main content area.
 * Manages the state between agent hub view and individual agent views.
 */
import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil, map } from 'rxjs/operators';
import { AgentOrchestratorService, AI_WORKBENCH_AGENT_ID } from '../../services/agent-orchestrator/agent-orchestrator.service';
import { AgentHubService } from '../../services/agent-hub/agent-hub.service';
import { LoggerService } from '../../../../services/logger-services/logger.service';
import { GetAgentResponseHistoryService } from '../../services/agent-response-history';
import { GetAgentResponseHistoryRequest } from '../../models';
import { StreamingStateService } from '../../../../shared/services/streaming-state/streaming-state.service';
import { ChatAutoScrollService } from '../../../../shared/services/chat-auto-scroll/chat-auto-scroll.service';
import { environment } from '../../../../environments/environment';
import { PopupMessageEvent } from '../../../models/chat-sessions.models';
import { SessionHistoryComponent } from '../../../../shared/components/session-history/session-history.component';
import { AgentPromptPrefixService } from '../../services/agent-prompt-prefix/agent-prompt-prefix.service';
import { ConfigurationService } from '../../../../services/configuration/configuration.service';
import { WORKBENCH_CLOSE_POST_MESSAGE_TYPE } from '../../constants/workbench-post-message';
import { BrandThemeService } from '../../../../services/brand-theme/brand-theme.service';
import { AgentConfigService } from '../../services/agent-config/agent-config.service';
import {
  mapHistoryResponseToAgentMessage,
} from '../../services/ria-history-file-url';

@Component({
  selector: 'app-agent-workspace',
  templateUrl: './agent-workspace.component.html',
  styleUrls: ['./agent-workspace.component.scss']
})
export class AgentWorkspaceComponent implements OnInit, OnDestroy {

  /** Template/compare with orchestrator virtual id for AI Workbench iframe */
  readonly aiWorkbenchAgentId = AI_WORKBENCH_AGENT_ID;

  /** Workbench access */
  workbenchAccessAllowed = false;

  /**
   * Passed to Workbench context as `aiAssistant`: true when opened via ?route=workbench.
   */
  workbenchAiAssistant = false;

  /** Whether the sidebar is visible */
  @Input() isSidebarOpen: boolean = false;
  
  /** Whether loader should be shown */
  @Input() loaderShow: boolean = false;
  
  /** User's first name for personalization */
  @Input() userFName: string = '';
  
  /** Reference to parent chatbot component for callbacks */
  @Input() chatbotComponentRef: any;

  /** Emitted when sidebar toggle is requested */
  @Output() sidebarToggled = new EventEmitter<boolean>();

  /** Emitted when user wants to navigate back to main chatbot */
  @Output() exitWorkspace = new EventEmitter<void>();

  /** Reference to the session-history child for popup rendering */
  @ViewChild('sessionHistory') sessionHistoryComponent?: SessionHistoryComponent;

  /** Cleanup subject */
  private readonly destroy$ = new Subject<void>();

  /** Bound handler so add/removeEventListener removes the same reference */
  private readonly onWorkbenchWindowMessage = (event: MessageEvent): void => {
    this.handleWorkbenchClosePostMessage(event);
  };

  /** Current agent ID */
  currentAgentId: string = '';

  /** Whether showing the agent hub (all agents view) */
  isAgentHubMode: boolean = true;

  /** Current session/thread ID for sidebar highlight; updates when session is selected from header or sidebar */
  currentSessionId$ = this.agentOrchestrator.currentThreadId$.pipe(map(id => id ?? ''));

  /** Toast message flags - matching chatbot.component.ts */
  DeleteSuccessMessage: boolean = false;
  EditSuccessMessage: boolean = false;
  
  /** Error toast message flags */
  IsCustomErrorMessageVisible: boolean = false;
  CustomErrorMessageText: string = '';
  
  /** Share link copied state for the share popup */
  shareLinkCopied: boolean = false;

  constructor(
    private readonly agentOrchestrator: AgentOrchestratorService,
    private readonly agentHubService: AgentHubService,
    private readonly logger: LoggerService,
    private readonly getAgentResponseHistoryService: GetAgentResponseHistoryService,
    private readonly streamingStateService: StreamingStateService,
    private readonly chatAutoScrollService: ChatAutoScrollService,
    private readonly agentPromptPrefixService: AgentPromptPrefixService,
    private readonly configurationService: ConfigurationService,
    private readonly brandThemeService: BrandThemeService,
    private readonly agentConfigService: AgentConfigService,
    private readonly hostElementRef: ElementRef<HTMLElement>
  ) {}

  /**
   * Set the available agent IDs from feature flags
   * Called by ChatbotWrapperComponent after calculating the intersection
   * of enrolled agents and ACL-validated agents
   * 
   * @param agentIds - Array of agent IDs that are allowed by feature flags
   */
  setAvailableAgents(agentIds: string[]): void {
    this.logger.logInfo(`[AgentWorkspace] Setting available agents from feature flags: ${agentIds.join(', ')}`, 'setAvailableAgents');
    this.agentHubService.setAllowedAgentIds(agentIds);

    // Initialize prompt prefix service for all agents (including regulations_agent and via_agent)
    // This loads the enhancement prompts (regenerate/crisp/detailed) for each agent
    agentIds.forEach(agentId => {
      this.agentPromptPrefixService.initialize(agentId);
    });
  }

  /**
   * Whether the user may open the AI Workbench iframe (tenant flags ∩ privilege).
   */
  setWorkbenchAccessAllowed(allowed: boolean): void {
    this.workbenchAccessAllowed = !!allowed;
  }

  /**
   * Open AI Workbench when chatbot loads with ?route=workbench (Freya Fusion expand flow).
   */
  openWorkbenchFromDeepLink(): void {
    this.onNavigateToAiWorkbench(true);
  }

  ngOnInit(): void {
    this.logger.logInfo('[AgentWorkspace] Initializing', 'ngOnInit');
    
    // Subscribe to agent changes
    this.agentOrchestrator.currentAgentId$
      .pipe(takeUntil(this.destroy$))
      .subscribe(agentId => {
        this.currentAgentId = agentId;
        this.updateViewMode();
        this.applyBrandingForAgent(agentId);
      });

    // Subscribe to hub visibility
    this.agentOrchestrator.showAgentHub$
      .pipe(takeUntil(this.destroy$))
      .subscribe(showHub => {
        this.isAgentHubMode = showHub;
      });

    // Subscribe to new session creation so app-session-history in workspace updates when user asks a question
    this.agentOrchestrator.newSessionCreated$
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ threadName, threadId, agentId }: { threadName: string; threadId: string; agentId: string }) => {
        // Only add the session if it belongs to the currently displayed agent (prevents cross-agent leaking)
        if (agentId && agentId === this.currentAgentId && this.currentAgentId !== 'module' && this.currentAgentId !== 'agent_hub' && this.currentAgentId !== AI_WORKBENCH_AGENT_ID && this.sessionHistoryComponent) {
          this.sessionHistoryComponent.addNewThread(threadName, threadId);
        }
      });

    window.addEventListener('message', this.onWorkbenchWindowMessage);
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.onWorkbenchWindowMessage);
    this.destroy$.next();
    this.destroy$.complete();
    this.logger.logInfo('[AgentWorkspace] Destroyed', 'ngOnDestroy');
  }

  /**
   * Allowed origins for Workbench → chatbot postMessage (sender origin must match).
   * Uses AIWorkbenchUrl origin + AIWorkbenchPostMessageAllowedOrigins from config.json.
   */
  private allowedWorkbenchPostMessageOrigins(): Set<string> {
    const origins = new Set<string>();
    const cfg = this.configurationService.config;

    const addOriginString = (value: string): void => {
      const v = value.trim();
      if (!v) {
        return;
      }
      try {
        origins.add(new URL(v).origin);
      } catch {
        /* ignore invalid */
      }
    };

    const raw = cfg?.AIWorkbenchUrl;
    if (raw && typeof raw === 'string') {
      addOriginString(raw);
    }

    const extra = cfg?.AIWorkbenchPostMessageAllowedOrigins;
    if (Array.isArray(extra)) {
      for (const o of extra) {
        if (typeof o === 'string') {
          addOriginString(o);
        }
      }
    }

    return origins;
  }

  /**
   * React Workbench calls window.parent.postMessage when user returns to Freya chatbot.
   */
  private handleWorkbenchClosePostMessage(event: MessageEvent): void {
    if (!this.allowedWorkbenchPostMessageOrigins().has(event.origin)) {
      return;
    }
    const data = event.data;
    if (!data || typeof data !== 'object' || (data as { type?: string }).type !== WORKBENCH_CLOSE_POST_MESSAGE_TYPE) {
      return;
    }
    if (this.currentAgentId !== AI_WORKBENCH_AGENT_ID) {
      return;
    }

    this.logger.logInfo('[AgentWorkspace] Workbench close via postMessage — navigating to hub', 'handleWorkbenchClosePostMessage');

    if (this.streamingStateService.getCurrentStreamingState()) {
      this.agentOrchestrator.checkStreamingAndExecute(
        () => this.agentOrchestrator.navigateToAgentHub(),
        'agent_switch'
      );
      return;
    }
    this.agentOrchestrator.navigateToAgentHub();
  }

  /**
   * Update view mode based on current agent ID
   */
  private updateViewMode(): void {
    this.isAgentHubMode = !this.currentAgentId || 
                          this.currentAgentId === 'module' || 
                          this.currentAgentId === 'agent_hub';
  }

  private applyBrandingForAgent(agentId: string): void {
    const brandingEnabled = this.agentConfigService.isBrandingEnabledForAgent(agentId);
    void this.brandThemeService.applyBrandingForAgent(this.hostElementRef.nativeElement, brandingEnabled);
  }

  /**
   * Toggle sidebar visibility
   */
  toggleSidebar(): void {
    this.isSidebarOpen = !this.isSidebarOpen;
    this.sidebarToggled.emit(this.isSidebarOpen);
  }

  /**
   * Close sidebar when opening source panel (view all sources) – same behavior as chatbot.
   */
  closeSidebarForSourcePanel(): void {
    this.isSidebarOpen = false;
    this.sidebarToggled.emit(false);
  }

  /**
   * Handle navigation to agent hub
   * Checks streaming state and shows confirmation popup if a response is being streamed
   */
  onNavigateToHub(): void {
    // Check if streaming is active - show confirmation popup before navigating
    if (this.streamingStateService.getCurrentStreamingState()) {
      this.logger.logInfo('[AgentWorkspace] Streaming active - requesting confirmation before navigating to hub', 'onNavigateToHub');
      this.agentOrchestrator.checkStreamingAndExecute(
        () => this.agentOrchestrator.navigateToAgentHub(),
        'agent_switch'
      );
      return;
    }
    
    this.agentOrchestrator.navigateToAgentHub();
  }

  /**
   * Navigate to AI Workbench iframe (URL from config).
   * @param fromDeepLink when true, context sends aiAssistant: true to Workbench
   */
  onNavigateToAiWorkbench(fromDeepLink = false): void {
    this.workbenchAiAssistant = fromDeepLink;
    if (!this.workbenchAccessAllowed) {
      return;
    }
    if (this.streamingStateService.getCurrentStreamingState()) {
      this.logger.logInfo('[AgentWorkspace] Streaming active - requesting confirmation before AI Workbench', 'onNavigateToAiWorkbench');
      this.agentOrchestrator.checkStreamingAndExecute(
        () => this.agentOrchestrator.navigateToAiWorkbench(),
        'agent_switch'
      );
      return;
    }
    this.agentOrchestrator.navigateToAiWorkbench();
  }

  /**
   * Handle agent selection from sidebar or hub
   * Checks streaming state and shows confirmation popup only when switching to a different agent
   */
  onAgentSelected(agentId: string): void {
    this.logger.logInfo(`[AgentWorkspace] Agent selected: ${agentId}`, 'onAgentSelected');
    
    // Same agent re-selected (e.g. click Via while already on Via) – no popup, no switch
    if (agentId === this.currentAgentId) {
      return;
    }
    
    // Check if streaming is active - show confirmation popup only when switching to another agent
    if (this.streamingStateService.getCurrentStreamingState()) {
      this.logger.logInfo(`[AgentWorkspace] Streaming active - requesting confirmation before switching to ${agentId}`, 'onAgentSelected');
      this.agentOrchestrator.checkStreamingAndExecute(
        () => this.agentOrchestrator.switchToAgent(agentId),
        'agent_switch'
      );
      return;
    }
    
    this.agentOrchestrator.switchToAgent(agentId);
  }

  /**
   * Handle exit from agent workspace
   */
  onExitWorkspace(): void {
    this.exitWorkspace.emit();
  }

  /**
   * Check if we're in individual agent mode (not hub)
   */
  get isInAgentMode(): boolean {
    return this.currentAgentId !== '' && 
           this.currentAgentId !== 'module' && 
           this.currentAgentId !== 'agent_hub' &&
           this.currentAgentId !== AI_WORKBENCH_AGENT_ID;
  }

  /** Whether a response is currently streaming (for session-history disable during streaming) */
  get isResponseStreaming(): boolean {
    return this.streamingStateService.getCurrentStreamingState();
  }

  /**
   * Get current agent session/thread ID for session-history selection state
   */
  getCurrentAgentSessionId(): string {
    return this.agentOrchestrator.getCurrentThreadId() || '';
  }

  /**
   * Handle session selection from chat history sidebar.
   * Loads thread history and updates orchestrator so the chat area displays it.
   */
  onAgentSessionSelected(thread: { thread_id: string }): void {
    if (!thread?.thread_id) {
      this.logger.logError('[AgentWorkspace] No thread_id provided', 'onAgentSessionSelected');
      return;
    }
    const threadId = thread.thread_id;
    this.chatAutoScrollService.cleanup(false);
    this.chatAutoScrollService.resetScrollState();
    const userID = environment.LoginUserID || '';
    const tenantID = environment.TenantId || '';

    this.agentOrchestrator.setLoadingSession(true);
    this.agentOrchestrator.updateLazyLoadState(true, true);
    this.agentOrchestrator.setCurrentThreadId(threadId);
    this.agentOrchestrator.setIsNewChat(false);
    // Clear so previous session messages don't show while loading; avoids last Q&A from old session appearing
    this.agentOrchestrator.setSessionLoadInProgress(true); // No animation when loaded history arrives
    this.agentOrchestrator.updateChatHistory([]);

    const request: GetAgentResponseHistoryRequest = {
      thread_id: threadId,
      user_id: userID,
      tenant_id: tenantID,
      page: 1,
      page_size: 5
    };

    this.getAgentResponseHistoryService.getResponseHistory(request, this.currentAgentId).subscribe({
      next: (response) => {
        // Only apply if still on this thread (avoid overwriting with stale response when user switched again)
        if (this.agentOrchestrator.getCurrentThreadId() !== threadId) {
          this.agentOrchestrator.setLoadingSession(false);
          this.agentOrchestrator.updateLazyLoadState(false, false);
          return;
        }
        if (response.status === 'success' && response.data) {
          const historyMessages = (response.data.responses || [])
            .map((resp: any) => mapHistoryResponseToAgentMessage(resp))
            .reverse();

          if (this.agentOrchestrator.getCurrentThreadId() !== threadId) {
            this.agentOrchestrator.setLoadingSession(false);
            this.agentOrchestrator.updateLazyLoadState(false, false);
            return;
          }

          if (historyMessages.length === 0) {
            this.agentOrchestrator.setIsNewChat(true);
            this.agentOrchestrator.showAgentLandingPage();
          } else {
            this.agentOrchestrator.updateChatHistory(historyMessages);
            this.agentOrchestrator.hideAgentLanding();
          }
          const totalPages = response.data!.pagination?.total_pages ?? 1;
          const currentPage = response.data!.pagination?.current_page ?? 1;
          this.agentOrchestrator.updateHistoryPagination(currentPage, totalPages);
          this.agentOrchestrator.updateLazyLoadState(false, currentPage < totalPages);
          this.agentOrchestrator.setLoadingSession(false);
        } else {
          // Non-success (e.g. validation error) – clear lazy load spinner
          this.logger.logError(`[AgentWorkspace] Response history returned non-success: ${response.message || response.status}`, 'onAgentSessionSelected');
          this.agentOrchestrator.updateLazyLoadState(false, false);
          this.agentOrchestrator.setSessionLoadInProgress(false);
          this.agentOrchestrator.setLoadingSession(false);
        }
      },
      error: (err) => {
        this.logger.logError(`[AgentWorkspace] Failed to load thread history: ${err?.message || err}`, 'onAgentSessionSelected');
        this.agentOrchestrator.updateLazyLoadState(false, false);
        this.agentOrchestrator.setSessionLoadInProgress(false);
        this.agentOrchestrator.setLoadingSession(false);
      }
    });
  }

  /**
   * Handle new chat request from session history (e.g. New Topic).
   * Resets orchestrator state so the chat area shows the landing page.
   */
  onAgentNewChat(): void {
    this.agentOrchestrator.checkStreamingAndExecute(() => {
      this.agentOrchestrator.clearAgentChatHistory();
      this.agentOrchestrator.resetAgentState();
    }, 'new_chat');
  }

  /** Close all toast messages */
  CloseSuccessMessage(): void {
    this.DeleteSuccessMessage = false;
    this.EditSuccessMessage = false;
  }

  /** Show delete success toast - matching chatbot.component.ts */
  onShowDeleteSuccessMessage(): void {
    this.DeleteSuccessMessage = true;
    setTimeout(() => {
      this.DeleteSuccessMessage = false;
    }, 4000);
  }

  /** Show edit/rename success toast - matching chatbot.component.ts */
  onShowEditSuccessMessage(): void {
    this.EditSuccessMessage = true;
    setTimeout(() => {
      this.EditSuccessMessage = false;
    }, 4000);
  }

  /** Handle popup messages from session-history */
  onShowPopupMessage(event: PopupMessageEvent): void {
    this.IsCustomErrorMessageVisible = true;
    this.CustomErrorMessageText = event.errorMessage || 'An error occurred. Please try again.';
    setTimeout(() => {
      this.IsCustomErrorMessageVisible = false;
      this.CustomErrorMessageText = '';
    }, 15000);
  }
  
  /** Handle keydown on popup backdrop (Escape to close delete/rename/share popup). */
  onPopupBackdropKeydown(event: KeyboardEvent, actionItem: any): void {
    if (event.key === 'Escape') {
      if (actionItem?.isDeleting && this.sessionHistoryComponent) {
        this.sessionHistoryComponent.cancelDelete(actionItem);
      } else if (actionItem?.is_edited && this.sessionHistoryComponent) {
        this.sessionHistoryComponent.cancelRename(actionItem);
      } else if (actionItem?.is_shared) {
        this.onCancelShare(actionItem);
      }
    }
  }

  /** Wrapper for session-history cancelShare to also reset shareLinkCopied state */
  onCancelShare(actionItem: any): void {
    this.shareLinkCopied = false;
    if (this.sessionHistoryComponent) {
      this.sessionHistoryComponent.cancelShare(actionItem);
    }
  }
  
  /** Wrapper for session-history copyShareLink to ensure proper state management */
  onCopyShareLink(actionItem: any): void {
    if (this.sessionHistoryComponent) {
      // Call the copy method
      this.sessionHistoryComponent.copyShareLink(actionItem);
      
      // Set shareLinkCopied to true after successful copy
      // The session-history component will handle the actual clipboard copy
      setTimeout(() => {
        this.shareLinkCopied = true;
        
        // Reset after 2 seconds
        setTimeout(() => {
          this.shareLinkCopied = false;
        }, 2000);
      }, 100);
    }
  }
}
